<template>
  <div class="cms-media-page">
    <cms-page-header
      :title="$t('cms.media.title')"
      :subtitle="$t('cms.media.subtitle')"
      :show-create="false"
    >
      <template #actions>
        <cms-button variant="primary" icon="upload" @click="uploadMedia">
          {{ $t('cms.media.upload') }}
        </cms-button>
      </template>
    </cms-page-header>

    <cms-filter-bar>
      <cms-filter-group :label="$t('cms.media.filterByType')">
        <select v-model="selectedType" class="cms-select" @change="loadMedia">
          <option value="">{{ $t('general.all') }}</option>
          <option value="image">{{ $t('cms.media.types.image') }}</option>
          <option value="video">{{ $t('cms.media.types.video') }}</option>
          <option value="document">{{ $t('cms.media.types.document') }}</option>
        </select>
      </cms-filter-group>

      <cms-filter-group :label="$t('cms.media.search')">
        <input
          v-model="searchQuery"
          type="text"
          class="cms-input"
          :placeholder="$t('cms.media.searchPlaceholder')"
          @input="debouncedSearch"
        />
      </cms-filter-group>

      <cms-filter-group :label="''">
        <cms-button variant="secondary" icon="refresh" :disabled="isLoading" @click="loadMedia">
          {{ $t('general.refresh') }}
        </cms-button>
      </cms-filter-group>
    </cms-filter-bar>

    <div v-if="isLoading" class="cms-media-page-loading">
      <div class="cms-loading-spinner" />
      <p>{{ $t('general.loading') }}</p>
    </div>

    <div v-else-if="errorMessage" class="cms-media-page-error">
      <p>{{ errorMessage }}</p>
      <button type="button" class="cms-btn-link" @click="loadMedia">
        {{ $t('general.retry') }}
      </button>
    </div>

    <cms-empty-state
      v-else-if="mediaList.length === 0"
      :message="$t('cms.media.emptyState')"
    />

    <cms-grid v-else min-width="200px" gap="md">
      <article
        v-for="media in mediaList"
        :key="media.id"
        class="cms-media-card"
        :class="[getMediaType(media), { 'is-menu-open': openMenuId === media.id }]"
      >
        <div class="cms-media-card-media">
          <button
            type="button"
            class="cms-media-card-preview"
            :aria-label="$t('cms.media.preview')"
            @click="openPreview(media)"
          >
            <img
              v-if="getMediaType(media) === 'image'"
              :src="getMediaUrl(media)"
              :alt="getMediaDisplayName(media)"
              class="cms-media-card-image"
            />
            <template v-else-if="getMediaType(media) === 'video'">
              <video
                :src="getMediaUrl(media)"
                class="cms-media-card-video"
                muted
                playsinline
                preload="metadata"
              />
              <span class="cms-media-card-video-play" aria-hidden="true">▶</span>
            </template>
            <div v-else class="cms-media-card-document">
              <v-icon icon="file" size="xl" />
              <span>{{ getFileExtension(media.fileName || media.file_name || media.name || '') }}</span>
            </div>
            <span
              class="cms-media-card-type-label"
              :class="`cms-media-card-type-label--${getMediaType(media)}`"
            >
              {{ getMediaTypeLabel(media) }}
            </span>
            <span class="cms-media-card-preview-overlay" aria-hidden="true">
              <v-icon icon="eye" size="md" />
            </span>
          </button>

          <div class="cms-media-card-menu">
            <button
              type="button"
              class="cms-media-card-menu-trigger"
              :aria-label="$t('cms.media.moreActions')"
              :aria-expanded="openMenuId === media.id"
              @click.stop="toggleMenu(media.id)"
            >
              <span class="cms-media-card-menu-dots" aria-hidden="true" />
            </button>

            <div
              v-if="openMenuId === media.id"
              class="cms-media-card-menu-dropdown"
              role="menu"
              @click.stop
            >
              <button
                type="button"
                class="cms-media-card-menu-item"
                role="menuitem"
                @click="handleMenuAction(media, 'preview')"
              >
                <v-icon icon="eye" size="sm" />
                <span>{{ $t('cms.media.preview') }}</span>
              </button>
              <button
                type="button"
                class="cms-media-card-menu-item"
                role="menuitem"
                @click="handleMenuAction(media, 'copy')"
              >
                <v-icon icon="link" size="sm" />
                <span>{{ $t('cms.media.copyUrl') }}</span>
              </button>
              <button
                type="button"
                class="cms-media-card-menu-item"
                role="menuitem"
                :disabled="downloadingId === media.id"
                @click="handleMenuAction(media, 'download')"
              >
                <v-icon icon="download" size="sm" />
                <span>
                  {{ downloadingId === media.id ? $t('cms.media.downloading') : $t('cms.media.download') }}
                </span>
              </button>
            </div>
          </div>
        </div>

        <button
          type="button"
          class="cms-media-card-info"
          :title="getMediaDisplayName(media)"
          @click="openPreview(media)"
        >
          <h3 class="cms-media-card-name">{{ getMediaDisplayName(media) }}</h3>
          <p class="cms-media-card-meta">
            {{ formatFileSize(media.size) }} · {{ formatDate(media.createdAt || media.created_at || '') }}
          </p>
        </button>
      </article>
    </cms-grid>

    <cms-pagination
      v-if="paginationMeta"
      id="cms-media-pagination"
      :meta="paginationMeta"
      :per-page-options="[18, 36, 54, 72]"
      @page-changed="handlePageChange"
      @per-page-changed="handlePerPageChange"
    />

    <div
      v-if="previewMedia"
      class="cms-media-preview-overlay"
      role="dialog"
      aria-modal="true"
      :aria-label="$t('cms.media.previewTitle')"
      @click="closePreview"
    >
      <div class="cms-media-preview-modal" @click.stop>
        <div class="cms-media-preview-header">
          <div class="cms-media-preview-header-text">
            <h3>{{ getMediaDisplayName(previewMedia) }}</h3>
            <p class="cms-media-preview-subtitle">
              {{ getMediaTypeLabel(previewMedia) }} · {{ formatFileSize(previewMedia.size) }} ·
              {{ formatDate(previewMedia.createdAt || previewMedia.created_at || '') }}
            </p>
          </div>
          <cms-icon-button icon="x" variant="default" size="sm" @click="closePreview" />
        </div>

        <div class="cms-media-preview-body">
          <img
            v-if="getMediaType(previewMedia) === 'image'"
            :src="getMediaUrl(previewMedia)"
            :alt="getMediaDisplayName(previewMedia)"
            class="cms-media-preview-image"
          />
          <video
            v-else-if="getMediaType(previewMedia) === 'video'"
            :src="getMediaUrl(previewMedia)"
            class="cms-media-preview-video"
            controls
            autoplay
            playsinline
          />
          <div v-else class="cms-media-preview-document">
            <v-icon icon="file" size="xl" />
            <p class="cms-media-preview-document-name">{{ getMediaDisplayName(previewMedia) }}</p>
            <p class="cms-media-preview-document-meta">
              {{ getFileExtension(previewMedia.fileName || previewMedia.file_name || previewMedia.name || '') }}
              · {{ formatFileSize(previewMedia.size) }}
            </p>
          </div>
        </div>

        <div class="cms-media-preview-footer">
          <cms-button variant="secondary" size="sm" icon="link" @click="copyUrl(previewMedia)">
            {{ $t('cms.media.copyUrl') }}
          </cms-button>
          <cms-button
            variant="primary"
            size="sm"
            icon="download"
            :disabled="downloadingId === previewMedia.id"
            @click="downloadMedia(previewMedia)"
          >
            {{ downloadingId === previewMedia.id ? $t('cms.media.downloading') : $t('cms.media.download') }}
          </cms-button>
          <cms-button
            v-if="getMediaUrl(previewMedia)"
            variant="secondary"
            size="sm"
            icon="export"
            @click="openInNewTab(previewMedia)"
          >
            {{ $t('cms.media.openInNewTab') }}
          </cms-button>
        </div>
      </div>
    </div>

    <div v-if="showUploadModal" class="cms-media-modal-overlay" @click="closeUploadModal">
      <div class="cms-media-modal" @click.stop>
        <div class="cms-media-modal-header">
          <h3>{{ $t('cms.media.uploadMedia') }}</h3>
          <cms-icon-button icon="x" variant="default" size="sm" @click="closeUploadModal" />
        </div>

        <div class="cms-media-modal-body">
          <p class="cms-media-upload-hint">{{ $t('cms.media.uploadHint') }}</p>
          <v-uploader
            ref="uploader"
            :key="uploadModalKey"
            :initial-value="[]"
            :multiple="true"
            :route-prefix="routePrefix"
            :enable-library="false"
            :after-upload="handleAfterTempUpload"
            @change="syncStagedUploads"
            @removed="syncStagedUploads"
          />
        </div>

        <div class="cms-media-modal-footer">
          <cms-button variant="secondary" :disabled="isConfirmingUpload" @click="closeUploadModal">
            {{ $t('general.button.close') }}
          </cms-button>
          <cms-button
            variant="primary"
            icon="upload"
            :disabled="isConfirmingUpload"
            @click="finishUpload"
          >
            {{ isConfirmingUpload ? $t('general.button.saving') : $t('cms.media.finishUpload') }}
          </cms-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toastification';
import { useAuthStore } from '@/stores/auth';
import api from '@/api';
import { normalizeResource } from '@/composables/useCmsActivityData';
import { extractCmsListData, extractCmsPaginationMeta } from '@/helpers/cmsPagination';
import { buildMediaConfirmEntries, dedupeMediaList, isPendingMediaUploadItem } from '@/helpers/cmsMediaUpload';
import { withCmsMediaSaving } from '@/helpers/cmsMediaSavingUi';
import type { CmsPaginationMeta } from '@/helpers/cmsPagination';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import VUploader from '@/components/vendor/upload/VUploader.vue';
import {
  CmsPageHeader,
  CmsFilterBar,
  CmsFilterGroup,
  CmsButton,
  CmsGrid,
  CmsEmptyState,
  CmsIconButton,
  CmsPagination,
} from '@/components/cms';

interface Media {
  id: number;
  name: string;
  fileName?: string;
  file_name?: string;
  mimeType?: string;
  mime_type?: string;
  size: number;
  createdAt?: string;
  created_at?: string;
  originalUrl?: string;
  original_url?: string;
  storageUrl?: string;
  storage_url?: string;
  url?: string;
  previewUrl?: string;
  preview_url?: string;
}

const MEDIA_DRIVER = 's3';
const LIBRARY_COLLECTION = 'cms_library';
const CMS_SETTINGS_MODEL_TYPE = 'cms_settings';

function mapMediaRecord(item: any): Media {
  const normalized = normalizeResource(item) || item;
  return {
    id: normalized.id,
    name: normalized.name,
    fileName: normalized.fileName ?? normalized.file_name,
    file_name: normalized.file_name ?? normalized.fileName,
    mimeType: normalized.mimeType ?? normalized.mime_type,
    mime_type: normalized.mime_type ?? normalized.mimeType,
    size: normalized.size ?? 0,
    createdAt: normalized.createdAt ?? normalized.created_at,
    created_at: normalized.created_at ?? normalized.createdAt,
    originalUrl: normalized.originalUrl ?? normalized.original_url,
    original_url: normalized.original_url ?? normalized.originalUrl,
    storageUrl: normalized.storageUrl ?? normalized.storage_url,
    storage_url: normalized.storage_url ?? normalized.storageUrl,
    url: normalized.url,
    previewUrl: normalized.previewUrl ?? normalized.preview_url,
    preview_url: normalized.preview_url ?? normalized.previewUrl,
  };
}

export default defineComponent({
  name: 'TheCmsMedia',
  components: {
    VIcon,
    VUploader,
    CmsPageHeader,
    CmsFilterBar,
    CmsFilterGroup,
    CmsButton,
    CmsGrid,
    CmsEmptyState,
    CmsIconButton,
    CmsPagination,
  },
  setup() {
    const { t } = useI18n();
    const toast = useToast();
    const authStore = useAuthStore();
    const agencySlug = computed(() => authStore.agency?.slug ?? null);

    const mediaList = ref<Media[]>([]);
    const isLoading = ref(false);
    const errorMessage = ref('');
    const showUploadModal = ref(false);
    const selectedType = ref('');
    const searchQuery = ref('');
    const currentPage = ref(1);
    const perPage = ref(18);
    const hasPendingUploads = ref(false);
    const stagedUploads = ref<any[]>([]);
    const paginationMeta = ref<CmsPaginationMeta | null>(null);
    const uploader = ref<any>(null);
    const cmsSettingsId = ref<number | null>(null);
    const isConfirmingUpload = ref(false);
    const uploadModalKey = ref(0);
    const previewMedia = ref<Media | null>(null);
    const openMenuId = ref<number | null>(null);
    const downloadingId = ref<number | null>(null);
    const routePrefix = 'api/s3';

    let searchTimeout: ReturnType<typeof setTimeout> | null = null;

    const loadCmsSettingsId = async () => {
      if (!agencySlug.value) {
        return;
      }

      try {
        const response = await api.settingsService.getCmsSettings(agencySlug.value);
        const data = response?.data || response || {};
        cmsSettingsId.value = data.id ?? null;
      } catch (error) {
        console.error('Error loading CMS settings for media library:', error);
      }
    };

    const getMediaUrl = (media: Media): string => {
      return media.originalUrl || media.original_url ||
        media.storageUrl || media.storage_url ||
        media.url ||
        media.previewUrl || media.preview_url || '';
    };

    const getMediaType = (media: Media): 'image' | 'video' | 'document' => {
      const mimeType = media.mimeType || media.mime_type;
      if (!mimeType) return 'document';
      if (mimeType.startsWith('image/')) return 'image';
      if (mimeType.startsWith('video/')) return 'video';
      return 'document';
    };

    const getFileExtension = (fileName: string): string => {
      if (!fileName) return '';
      const parts = fileName.split('.');
      return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : '';
    };

    const getMediaDisplayName = (media: Media): string => {
      return media.name || media.fileName || media.file_name || t('cms.media.types.document');
    };

    const getMediaTypeLabel = (media: Media): string => {
      const type = getMediaType(media);
      return t(`cms.media.types.${type}`);
    };

    const closeMenu = () => {
      openMenuId.value = null;
    };

    const toggleMenu = (id: number) => {
      openMenuId.value = openMenuId.value === id ? null : id;
    };

    const handleMenuAction = (media: Media, action: 'preview' | 'copy' | 'download') => {
      closeMenu();
      if (action === 'preview') {
        openPreview(media);
      } else if (action === 'copy') {
        copyUrl(media);
      } else {
        downloadMedia(media);
      }
    };

    const openPreview = (media: Media) => {
      closeMenu();
      previewMedia.value = media;
    };

    const closePreview = () => {
      previewMedia.value = null;
    };

    const openInNewTab = (media: Media) => {
      const url = getMediaUrl(media);
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }
      if (previewMedia.value) {
        closePreview();
      } else if (openMenuId.value !== null) {
        closeMenu();
      } else if (showUploadModal.value) {
        closeUploadModal();
      }
    };

    const handleDocumentClick = () => {
      if (openMenuId.value !== null) {
        closeMenu();
      }
    };

    const loadMedia = async () => {
      if (!agencySlug.value) {
        errorMessage.value = t('cms.media.agencyNotFound');
        return;
      }

      isLoading.value = true;
      errorMessage.value = '';

      try {
        const filters: Record<string, any> = {
          perPage: perPage.value,
          page: currentPage.value,
        };

        if (selectedType.value) {
          filters.filters = { type: selectedType.value };
        }

        if (searchQuery.value) {
          if (!filters.filters) filters.filters = {};
          filters.filters.search = searchQuery.value;
        }

        const response = await api.media.list(agencySlug.value, filters);

        const list = extractCmsListData(response);
        mediaList.value = dedupeMediaList(
          list
            .map((item) => mapMediaRecord(item))
            .filter((item) => item.id != null),
        );

        const responseMeta = response?.meta ?? response?.data?.meta;
        const meta = extractCmsPaginationMeta(response, {
          currentPage: currentPage.value,
          perPage: perPage.value,
        });

        if (meta) {
          paginationMeta.value = meta;
          currentPage.value = meta.currentPage;
          perPage.value = meta.perPage;
        } else if (responseMeta) {
          paginationMeta.value = {
            currentPage: Number(responseMeta.currentPage ?? responseMeta.current_page) || currentPage.value,
            lastPage: Number(responseMeta.lastPage ?? responseMeta.last_page) || 1,
            perPage: Number(responseMeta.perPage ?? responseMeta.per_page) || perPage.value,
            from: responseMeta.from ?? null,
            to: responseMeta.to ?? null,
            total: Number(responseMeta.total) || 0,
          };
          currentPage.value = paginationMeta.value.currentPage;
          perPage.value = paginationMeta.value.perPage;
        } else {
          paginationMeta.value = null;
        }
      } catch (error: any) {
        console.error('Error loading media:', error);
        errorMessage.value = error?.message || t('cms.media.loadFailed');
        mediaList.value = [];
      } finally {
        isLoading.value = false;
      }
    };

    const debouncedSearch = () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      searchTimeout = setTimeout(() => {
        currentPage.value = 1;
        loadMedia();
      }, 500);
    };

    const handlePageChange = (page: number) => {
      currentPage.value = page;
      loadMedia();
    };

    const handlePerPageChange = (newPerPage: number) => {
      perPage.value = newPerPage;
      currentPage.value = 1;
      loadMedia();
    };

    const isStagedUploadItem = isPendingMediaUploadItem;

    const mergeStagedUploads = (incoming: any[] = []) => {
      incoming.filter(isStagedUploadItem).forEach((item) => {
        const uuid = item?.attributes?.uuid ?? item?.uuid;
        if (!uuid) return;
        const exists = stagedUploads.value.some(
          (existing) => (existing?.attributes?.uuid ?? existing?.uuid) === uuid,
        );
        if (!exists) {
          stagedUploads.value.push(item);
        }
      });
      hasPendingUploads.value = stagedUploads.value.length > 0;
    };

    const syncStagedUploads = async () => {
      await nextTick();
      const fromUploader = uploader.value?.mediaLibrary?.state?.media ?? [];
      mergeStagedUploads(Array.isArray(fromUploader) ? fromUploader : []);
    };

    const resolveUploadItemFromState = (uuid: string) => {
      const items = uploader.value?.mediaLibrary?.state?.media ?? [];
      return items.find(
        (item: any) => (item?.attributes?.uuid ?? item?.uuid) === uuid,
      );
    };

    const handleAfterTempUpload = async (result: any) => {
      if (result?.success === false) {
        hasPendingUploads.value = stagedUploads.value.length > 0;
        return;
      }

      await nextTick();

      const uuid = result?.uuid ?? result?.attributes?.uuid;
      if (uuid) {
        const fromState = resolveUploadItemFromState(uuid);
        mergeStagedUploads([fromState ?? { uuid }]);
        return;
      }

      const batch = result
        ? (Array.isArray(result) ? result : [result])
        : [];
      if (batch.length > 0) {
        mergeStagedUploads(batch);
      } else {
        await syncStagedUploads();
      }
    };

    const uploadMedia = async () => {
      uploadModalKey.value += 1;
      stagedUploads.value = [];
      hasPendingUploads.value = false;
      if (!cmsSettingsId.value) {
        await loadCmsSettingsId();
      }
      showUploadModal.value = true;
    };

    const closeUploadModal = () => {
      showUploadModal.value = false;
      stagedUploads.value = [];
      hasPendingUploads.value = false;
    };

    const getPendingUploadItems = () => {
      if (stagedUploads.value.length > 0) {
        return stagedUploads.value.filter(isStagedUploadItem);
      }
      const fromUploader = uploader.value?.mediaLibrary?.state?.media ?? [];
      return (Array.isArray(fromUploader) ? fromUploader : []).filter(isStagedUploadItem);
    };

    const confirmUploads = async () => {
      if (!cmsSettingsId.value) {
        toast.error(t('cms.media.settingsNotLoaded'));
        return false;
      }

      const pendingItems = getPendingUploadItems();
      const mediaEntries = buildMediaConfirmEntries(pendingItems, LIBRARY_COLLECTION);

      if (mediaEntries.length === 0) {
        toast.error(t('cms.media.noFilesToSave'));
        return false;
      }

      isConfirmingUpload.value = true;

      try {
        await withCmsMediaSaving(async () => {
          await api.media.confirm(MEDIA_DRIVER, {
            media: mediaEntries,
            model_type: CMS_SETTINGS_MODEL_TYPE,
            model_id: cmsSettingsId.value,
            collection: LIBRARY_COLLECTION,
          });
          await loadMedia();
        });
        toast.success(t('cms.media.uploadSuccess'));
        return true;
      } catch (error: any) {
        console.error('Error confirming media upload:', error);
        toast.error(error?.response?.data?.message || error?.message || t('cms.media.uploadFailed'));
        return false;
      } finally {
        isConfirmingUpload.value = false;
      }
    };

    const finishUpload = async () => {
      await syncStagedUploads();
      const confirmed = await confirmUploads();
      if (confirmed) {
        closeUploadModal();
      }
    };

    const copyUrl = async (media: Media) => {
      const url = getMediaUrl(media);
      if (!url) return;

      try {
        await navigator.clipboard.writeText(url);
        toast.success(t('cms.media.copyUrlSuccess'));
      } catch (err) {
        console.error('Failed to copy URL:', err);
        toast.error(t('cms.media.copyUrlFailed'));
      }
    };

    const downloadMedia = async (media: Media) => {
      downloadingId.value = media.id;

      try {
        const downloadUrl = await api.media.download(MEDIA_DRIVER, media.id);
        const url = typeof downloadUrl === 'string'
          ? downloadUrl
          : downloadUrl?.url || downloadUrl?.data || getMediaUrl(media);

        if (!url) {
          toast.error(t('cms.media.downloadFailed'));
          return;
        }

        window.open(url, '_blank', 'noopener,noreferrer');
      } catch (error) {
        console.error('Error downloading media:', error);
        const fallbackUrl = getMediaUrl(media);
        if (fallbackUrl) {
          window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
        } else {
          toast.error(t('cms.media.downloadFailed'));
        }
      } finally {
        downloadingId.value = null;
      }
    };

    const formatFileSize = (bytes: number) => {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
    };

    const formatDate = (dateString: string) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return '';
      return date.toLocaleDateString();
    };

    watch(selectedType, () => {
      currentPage.value = 1;
      loadMedia();
    });

    onMounted(async () => {
      document.addEventListener('keydown', handleEscapeKey);
      document.addEventListener('click', handleDocumentClick);
      await loadCmsSettingsId();
      await loadMedia();
    });

    onUnmounted(() => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.removeEventListener('click', handleDocumentClick);
    });

    return {
      mediaList,
      isLoading,
      errorMessage,
      showUploadModal,
      selectedType,
      searchQuery,
      paginationMeta,
      uploader,
      isConfirmingUpload,
      hasPendingUploads,
      uploadModalKey,
      handleAfterTempUpload,
      syncStagedUploads,
      previewMedia,
      openMenuId,
      downloadingId,
      routePrefix,
      getMediaUrl,
      getMediaType,
      getFileExtension,
      getMediaDisplayName,
      getMediaTypeLabel,
      toggleMenu,
      handleMenuAction,
      openPreview,
      closePreview,
      openInNewTab,
      loadMedia,
      debouncedSearch,
      handlePageChange,
      handlePerPageChange,
      uploadMedia,
      closeUploadModal,
      finishUpload,
      copyUrl,
      downloadMedia,
      formatFileSize,
      formatDate,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/components/cms/cms-styles.scss';

.cms-media-page-loading,
.cms-media-page-error {
  text-align: center;
  padding: $cms-spacing-xxl;
  color: $cms-text-secondary;
}

.cms-loading-spinner {
  border: 3px solid $cms-border-color-light;
  border-top: 3px solid $cms-primary-color;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: cms-media-spin 1s linear infinite;
  margin: 0 auto $cms-spacing-md;
}

@keyframes cms-media-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.cms-btn-link {
  background: none;
  border: none;
  color: $cms-primary-color;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: $cms-primary-dark;
  }
}

.cms-media-card {
  background: $cms-background-white;
  border-radius: $cms-radius-lg;
  overflow: hidden;
  border: 1px solid $cms-border-color-light;
  display: flex;
  flex-direction: column;
  transition: box-shadow $cms-transition-fast, transform $cms-transition-fast;

  &:hover,
  &.is-menu-open,
  &:focus-within {
    box-shadow: $cms-shadow-md;
    transform: translateY(-2px);

    .cms-media-card-menu-trigger {
      opacity: 1;
    }
  }

  &.image {
    border-top: 3px solid $cms-success-color;
  }

  &.video {
    border-top: 3px solid $cms-info-color;
  }

  &.document {
    border-top: 3px solid $cms-warning-color;
  }
}

.cms-media-card-media {
  position: relative;
}

.cms-media-card-preview {
  position: relative;
  height: 160px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: $cms-background-gray-light;
  border: none;
  padding: 0;
  cursor: pointer;
  overflow: hidden;

  &:hover .cms-media-card-preview-overlay,
  &:focus-visible .cms-media-card-preview-overlay {
    opacity: 1;
  }

  &:focus-visible {
    @include cms-focus-ring;
  }
}

.cms-media-card-image,
.cms-media-card-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
}

.cms-media-card-type-label {
  position: absolute;
  bottom: $cms-spacing-sm;
  left: $cms-spacing-sm;
  z-index: 1;
  padding: 0.15rem 0.5rem;
  border-radius: $cms-radius-sm;
  font-size: $cms-font-size-xs;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: white;
  pointer-events: none;
  line-height: 1.2;

  &--image {
    background: $cms-success-color;
  }

  &--video {
    background: $cms-info-color;
  }

  &--document {
    background: $cms-warning-color;
  }
}

.cms-media-card-video-play {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: white;
  font-size: 1.1rem;
  line-height: 1;
  padding-left: 3px;
  pointer-events: none;
  box-shadow: $cms-shadow-sm;
}

.cms-media-card-preview-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
  color: white;
  opacity: 0;
  transition: opacity $cms-transition-fast;
  pointer-events: none;
}

.cms-media-card-document {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $cms-spacing-xs;
  color: $cms-text-secondary;
  font-size: $cms-font-size-sm;
  pointer-events: none;
}

.cms-media-card-menu {
  position: absolute;
  top: $cms-spacing-sm;
  right: $cms-spacing-sm;
  z-index: 2;
}

.cms-media-card-menu-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: $cms-radius-md;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: $cms-shadow-sm;
  cursor: pointer;
  opacity: 0;
  transition: opacity $cms-transition-fast, background $cms-transition-fast;

  &:hover,
  &:focus-visible {
    background: $cms-background-white;
  }

  &:focus-visible {
    opacity: 1;
    @include cms-focus-ring;
  }
}

.cms-media-card-menu-dots {
  display: block;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: $cms-text-primary;
  box-shadow:
    0 -6px 0 $cms-text-primary,
    0 6px 0 $cms-text-primary;
}

.cms-media-card-menu-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  min-width: 180px;
  padding: $cms-spacing-xs;
  background: $cms-background-white;
  border: 1px solid $cms-border-color-light;
  border-radius: $cms-radius-md;
  box-shadow: $cms-shadow-md;
}

.cms-media-card-menu-item {
  display: flex;
  align-items: center;
  gap: $cms-spacing-sm;
  width: 100%;
  padding: $cms-spacing-sm $cms-spacing-md;
  border: none;
  border-radius: $cms-radius-sm;
  background: transparent;
  color: $cms-text-primary;
  font-size: $cms-font-size-sm;
  text-align: left;
  cursor: pointer;
  transition: background $cms-transition-fast;

  &:hover:not(:disabled) {
    background: $cms-background-gray-light;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:focus-visible {
    @include cms-focus-ring;
  }
}

.cms-media-card-info {
  display: block;
  width: 100%;
  padding: $cms-spacing-sm $cms-spacing-md;
  border: none;
  border-top: 1px solid $cms-border-color-light;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background $cms-transition-fast;

  &:hover {
    background: $cms-background-gray-light;
  }

  &:focus-visible {
    @include cms-focus-ring;
  }
}

.cms-media-card-name {
  font-size: $cms-font-size-sm;
  font-weight: 600;
  margin: 0 0 0.15rem;
  color: $cms-text-primary;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cms-media-card-meta {
  font-size: $cms-font-size-xs;
  color: $cms-text-secondary;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cms-media-preview-overlay {
  position: fixed;
  inset: 0;
  z-index: 1100;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: $cms-spacing-lg;
}

.cms-media-preview-modal {
  background: $cms-background-white;
  border-radius: $cms-radius-xl;
  width: min(960px, 100%);
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: $cms-shadow-lg;
}

.cms-media-preview-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: $cms-spacing-md;
  padding: $cms-spacing-lg;
  border-bottom: 1px solid $cms-border-color-light;

  h3 {
    margin: 0;
    font-size: $cms-font-size-lg;
    color: $cms-text-primary;
    word-break: break-word;
  }
}

.cms-media-preview-header-text {
  min-width: 0;
  flex: 1;
}

.cms-media-preview-subtitle {
  margin: $cms-spacing-xs 0 0;
  font-size: $cms-font-size-sm;
  color: $cms-text-secondary;
}

.cms-media-preview-body {
  flex: 1;
  min-height: 200px;
  max-height: calc(92vh - 180px);
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background: $cms-background-gray-light;
  padding: $cms-spacing-lg;
}

.cms-media-preview-image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border-radius: $cms-radius-md;
}

.cms-media-preview-video {
  width: 100%;
  max-height: 70vh;
  border-radius: $cms-radius-md;
  background: #000;
}

.cms-media-preview-document {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $cms-spacing-md;
  padding: $cms-spacing-xl;
  color: $cms-text-secondary;
  text-align: center;
}

.cms-media-preview-document-name {
  margin: 0;
  font-size: $cms-font-size-lg;
  font-weight: 600;
  color: $cms-text-primary;
  word-break: break-word;
}

.cms-media-preview-document-meta {
  margin: 0;
  font-size: $cms-font-size-sm;
}

.cms-media-preview-footer {
  display: flex;
  flex-wrap: wrap;
  gap: $cms-spacing-sm;
  justify-content: flex-end;
  padding: $cms-spacing-lg;
  border-top: 1px solid $cms-border-color-light;
}

.cms-media-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.cms-media-modal {
  background: $cms-background-white;
  border-radius: $cms-radius-xl;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  color: $cms-text-primary;
}

.cms-media-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: $cms-spacing-lg;
  border-bottom: 1px solid $cms-border-color-light;

  h3 {
    margin: 0;
    font-size: $cms-font-size-lg;
  }
}

.cms-media-modal-body {
  padding: $cms-spacing-lg;
}

.cms-media-upload-hint {
  margin: 0 0 $cms-spacing-md;
  font-size: $cms-font-size-sm;
  color: $cms-text-secondary;
}

.cms-media-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: $cms-spacing-sm;
  padding: $cms-spacing-lg;
  border-top: 1px solid $cms-border-color-light;
}

@media (max-width: $cms-breakpoint-mobile) {
  .cms-media-card-menu-trigger {
    opacity: 1;
  }

  .cms-media-preview-footer {
    flex-direction: column;

    :deep(.cms-btn) {
      width: 100%;
      justify-content: center;
    }
  }

  .cms-media-preview-overlay {
    padding: $cms-spacing-sm;
  }
}
</style>
